import { type MouseEvent, type ReactNode, useCallback } from "react";
import { changeSort } from "../../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "../../../../../redux/store";
import { type TabularColumnConfig } from "../../../../models";
import { PopperTooltip } from "../../../../../common/components/Tooltip";
import classNames from "classnames";
import { TableSortLabel } from "../../../../../common/components/Table/TableElements";
import { SortDirection } from "../../../../../common/hooks/useSorting";
import { useUnitMode } from "~/feature/UnitMode/useUnitMode";
import { UnitMode } from "~/feature/UnitMode/UnitMode";
import { Trans, useTranslation } from "react-i18next";

interface Props {
  label: ReactNode;
  hoverTitle?: ReactNode;
  sortDescCode?: string;
  sortAscCode?: string;
  alignment?: "left" | "right";
  "data-test-id": string;
  unit?: string;
  className?: string;
}

interface TechValHeaderCellProps {
  config: TabularColumnConfig;
  alignment?: "right" | "left";
  className?: string;
}

export const DesignationHeaderCell = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  return (
    <div className={classNames("d-flex", className)}>
      <HeaderCell
        label={t("page.search.tabular.product.title")}
        sortAscCode={"sxTitleAttribute-asc"}
        sortDescCode={"sxTitleAttribute-desc"}
        data-test-id={"product"}
      />
    </div>
  );
};

export const ProductComparisonHeaderCell = () => {
  const { t } = useTranslation();
  return <HeaderCell label={t("compare.label")} data-test-id={"product-comparison-list"} className="f-center" />;
};

export const WeightHeaderCell = () => {
  const { t } = useTranslation();
  const unitMode = useUnitMode();

  return (
    <HeaderCell
      label={
        unitMode === UnitMode.Metric
          ? `≈m (${t("page.selectionAssistant.tableHeader.mass.unit")})`
          : `≈m (${t("page.selectionAssistant.tableHeader.mass.imperial.unit")})`
      }
      hoverTitle={
        unitMode === UnitMode.Metric
          ? t("page.selectionAssistant.tableHeader.mass.label")
          : t("page.selectionAssistant.tableHeader.mass.imperial.label")
      }
      sortDescCode={"mass-desc"}
      sortAscCode={"mass-asc"}
      data-test-id={"mass"}
    />
  );
};

export const TechValHeaderCell = ({ config, alignment, className }: TechValHeaderCellProps) => {
  const { t } = useTranslation();

  const hoverTitle = t(config.hoverTitle);

  const labelUnit = config.unit ? ` ${config.unit}` : "";

  return (
    <HeaderCell
      alignment={alignment}
      hoverTitle={<span dangerouslySetInnerHTML={{ __html: hoverTitle + (config.unit ?? "") }} />}
      label={
        <span className={className}>
          <Trans i18nKey={config.name} />
          {labelUnit}
        </span>
      }
      sortDescCode={config.sortDesc}
      sortAscCode={config.sortAsc}
      data-test-id={config.techValueUID}
    />
  );
};

export const HeaderCell = ({
  alignment = "left",
  hoverTitle,
  label,
  sortAscCode,
  sortDescCode,
  className,
  "data-test-id": testId,
}: Props) => {
  const selectedSortCode = useSelector(selectSortCode);
  const selectedSortDirection = useSelector(selectSortDirection);

  const selectedSort = selectedSortDirection ? selectedSortCode + selectedSortDirection : selectedSortCode;
  const selectedAsc = selectedSort && selectedSort === sortAscCode;
  const selectedDesc = selectedSort && selectedSort === sortDescCode;

  const isSelected = selectedAsc || selectedDesc;

  const dispatch = useDispatch();

  const onClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const canClickAsc = sortAscCode && selectedSort !== sortAscCode;
      const canClickDesc = sortDescCode && selectedSort !== sortDescCode;
      const selectedAsc = selectedSort && selectedSort === sortAscCode;
      const selectedDesc = selectedSort && selectedSort === sortDescCode;
      if (canClickAsc && !selectedAsc && sortAscCode) {
        const parts = sortAscCode.split("-", 2);
        // @ts-ignore
        dispatch(changeSort(parts[0], parts[1] ? "-" + parts[1] : undefined));
      } else if (canClickDesc && !selectedDesc && sortDescCode) {
        const parts = sortDescCode.split("-", 2);
        // @ts-ignore
        dispatch(changeSort(parts[0], parts[1] ? "-" + parts[1] : undefined));
      }
    },
    [sortAscCode, sortDescCode, selectedSort],
  );

  const testIdId = `sort-btn-${testId}`;

  const testIdSorting = `sorting-${selectedDesc ? "descending" : "ascending"} ${isSelected ? "sorting-selected" : ""}`;

  const labelEl =
    !sortAscCode && !sortDescCode ? (
      <div data-test-id={testIdId} className={className}>
        {label}
      </div>
    ) : (
      <div data-test-id={testIdId} className={className}>
        <TableSortLabel
          onClick={onClick}
          active={!!isSelected}
          data-test-id={testIdSorting}
          direction={selectedDesc ? SortDirection.Desc : SortDirection.Asc}
          sx={{
            span: {
              order: alignment === "right" ? "2" : "1",
            },
          }}
        >
          {label}
        </TableSortLabel>
      </div>
    );

  if (hoverTitle) {
    return (
      <PopperTooltip tooltipContent={hoverTitle} className={"d-i-flex"}>
        {labelEl}
      </PopperTooltip>
    );
  }

  return labelEl;
};

const selectSortCode = (state: RootState) => state.srp.sort.selectedCode;
const selectSortDirection = (state: RootState) => state.srp.sort.selectedDirection;
